import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import { ErrorAlert } from '@repo/shared-client/components/blocks'
import { NativeSelectFormField } from '@repo/shared-client/components/fields'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Alert,
  AlertDescription,
  AlertTitle,
  AspectRatio,
  Badge,
  Button,
  Card,
  CardContent,
  Image,
  NativeSelect,
  NativeSelectOption,
  Separator,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@repo/shared-client/components/ui'
import { Form, useForm } from '@repo/shared-client/form'
import { cn } from '@repo/shared-client/lib/utils'
import { demographicIndicatesMinor } from '@repo/shared-common'
import { createFileRoute, stripSearchParams, useNavigate, useSearch } from '@tanstack/react-router'
import ReportCreate from '@web/src/features/report/report-create/report-create'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { PlayIcon } from 'lucide-react'
import type { ChangeEvent, CSSProperties } from 'react'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import SFPageLoading from 'src/components/primatives/sf-page-loading/sf-page-loading'
import SFProfileBanner from 'src/components/primatives/sf-profile-banner/sf-profile-banner'
import SimpleMediaImageViewer from 'src/features/media/simple-media-image-viewer/simple-media-image-viewer'
import { AttachedMediaInfo, MediaInfo, ProfileInfo, ProfileMediaInfo } from 'src/lib/fragments'
import { videoDurationStr } from 'src/lib/utilsClient'
import type {
  AdminReviewMediaQuery,
  AdminReviewMediaQueryVariables,
  AttachedMediaAdminApproveMutation,
  AttachedMediaAdminApproveMutationVariables,
  AttachedMediaFilter,
  AttachedMediaSortEnum,
  MediaAdminApproveMutation,
  MediaAdminApproveMutationVariables,
  MediaFilter,
  MediaSortEnum,
  NSFWRatingEnum,
} from 'types/graphql'
import { z } from 'zod'

const formatScanPayload = (t: (key: string) => string, results: unknown) => {
  return results == null ? t('No scanner results') : JSON.stringify(results, null, 2)
}

const ADMIN_REVIEW_MEDIA_QUERY: TypedDocumentNode<AdminReviewMediaQuery, AdminReviewMediaQueryVariables> = gql`
  query AdminReviewMediaQuery(
    $page: Int
    $filter: MediaFilter
    $sortBy: MediaSortEnum
    $attachedFilter: AttachedMediaFilter
    $attachedSortBy: AttachedMediaSortEnum
  ) {
    mediaList: mediaAdminListPage(page: $page, filter: $filter, sortBy: $sortBy) {
      medias {
        ...MediaInfo
        owner {
          ...ProfileInfo
        }
        albumName
        height
        nsfwRating
        nsfwScan {
          results
        }
        aiDetection {
          results
        }
        demographic {
          ageEstimation
          minorScore
          results
        }
      }
      count
      perPage
    }
    attachedMediaList: attachedMediaAdminListPage(page: $page, filter: $attachedFilter, sortBy: $attachedSortBy) {
      medias {
        ...AttachedMediaInfo
        owner {
          ...ProfileInfo
        }
        height
        nsfwScan {
          results
        }
        aiDetection {
          results
        }
        demographic {
          ageEstimation
          minorScore
          results
        }
      }
      count
      perPage
    }
  }
  ${MediaInfo}
  ${AttachedMediaInfo}
  ${ProfileInfo}
  ${ProfileMediaInfo}
`

const APPROVE_MEDIA: TypedDocumentNode<MediaAdminApproveMutation, MediaAdminApproveMutationVariables> = gql`
  mutation MediaAdminApprove($id: String!, $nsfwRating: NSFWRatingEnum!) {
    mediaAdminApprove(id: $id, nsfwRating: $nsfwRating) {
      ...MediaInfo
    }
  }
  ${MediaInfo}
`

type AttachedMediaApprovalDocument = TypedDocumentNode<
  AttachedMediaAdminApproveMutation,
  AttachedMediaAdminApproveMutationVariables
>

const APPROVE_ATTACHED_MEDIA: AttachedMediaApprovalDocument = gql`
  mutation AttachedMediaAdminApprove($id: String!, $nsfwRating: NSFWRatingEnum!) {
    attachedMediaAdminApprove(id: $id, nsfwRating: $nsfwRating) {
      ...AttachedMediaInfo
    }
  }
  ${AttachedMediaInfo}
`

const ReviewMediaCard = ({ media, isAttached = false }) => {
  const { t } = useTranslation()
  const [reportModal, setReportModal] = useState(false)
  const [mediaViewerOpen, setMediaViewerOpen] = useState(false)
  const [completed, setCompleted] = useState(false)
  const [selectedRating, setSelectedRating] = useState<NSFWRatingEnum>(media.nsfwRating || 'UNDEFINED')
  const [approvedRating, setApprovedRating] = useState<NSFWRatingEnum | null>(null)

  const [approveMedia] = useMutation(APPROVE_MEDIA, {
    onCompleted: () => {
      setCompleted(true)
      setApprovedRating(selectedRating)
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const [approveAttachedMedia] = useMutation(APPROVE_ATTACHED_MEDIA, {
    onCompleted: () => {
      setCompleted(true)
      setApprovedRating(selectedRating)
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const handleApproveMedia = (nsfwRating: NSFWRatingEnum) => {
    if (isAttached) {
      approveAttachedMedia({ variables: { id: media.id, nsfwRating: nsfwRating } })
    } else {
      approveMedia({ variables: { id: media.id, nsfwRating: nsfwRating } })
    }
  }

  const handleSaveNSFWRating = () => {
    if (selectedRating !== 'UNDEFINED') {
      handleApproveMedia(selectedRating)
    }
  }

  const handleRejectMedia = () => {
    setCompleted(true)
  }

  const handleImageClick = () => {
    setMediaViewerOpen(true)
  }

  useEffect(() => {
    if (!media.needsReview && media.reviewerId) {
      setCompleted(true)
      setApprovedRating(media.nsfwRating)
    }
  }, [media.needsReview, media.reviewerId, media.nsfwRating])

  useEffect(() => {
    setSelectedRating(media.nsfwRating || 'UNDEFINED')
  }, [media.nsfwRating])

  const ratio = media.width && media.height ? media.width / media.height : 16 / 9

  const ratingOptions = [
    { value: 'UNDEFINED', label: 'UNDEFINED' },
    { value: 'SFW', label: t('Safe For Work') },
    { value: 'SUGGESTIVE', label: t('Suggestive') },
    { value: 'EROTICA', label: t('Erotica') },
    { value: 'EXPLICIT', label: t('Explicit') },
  ]

  // Helper function to get rating display info
  const getRatingDisplayInfo = (rating: NSFWRatingEnum) => {
    switch (rating) {
      case 'SFW':
        return { label: 'SFW', boxClass: 'border-green-500 bg-green-600 text-white shadow-green-500/40' }
      case 'SUGGESTIVE':
        return {
          label: 'SUGGESTIVE',
          boxClass: 'border-yellow-500 bg-yellow-500 text-foreground shadow-yellow-500/40',
        }
      case 'EROTICA':
        return {
          label: 'EROTICA',
          boxClass: 'border-orange-500 bg-orange-500 text-white shadow-orange-500/40',
        }
      case 'EXPLICIT':
        return { label: 'EXPLICIT', boxClass: 'border-red-500 bg-red-600 text-white shadow-red-500/40' }
      default:
        return { label: 'UNDEFINED', boxClass: 'border-muted-foreground bg-muted text-foreground' }
    }
  }

  // Transform media for SimpleMediaImageViewer
  const mediaArray = [
    {
      id: media.id,
      fileName: media.fileName,
      mediaType: media.mediaType,
      reviewStatus: media.reviewStatus,
      imageUrlSmall: media.imageUrlSmall,
      imageUrlMedium: media.imageUrlMedium,
      imageUrlLarge: media.imageUrlLarge,
      videoPlaybackToken: media.videoPlaybackToken,
      videoThumbnailToken: media.videoThumbnailToken,
      aiScore: media.aiScore,
    },
  ]

  return (
    <>
      <Card className="flex w-full max-w-full self-stretch md:w-[48%]">
        <CardContent className="flex w-full flex-col gap-3">
          <SFProfileBanner
            lowerTitle={
              <span className="text-muted-foreground text-sm">
                {media.albumName != null
                  ? t('Album [{{name}}]', { name: media.albumName })
                  : isAttached
                    ? t('Attached media')
                    : ''}
              </span>
            }
            profile={media.owner}
          />

          <Separator className="w-full" />
          {demographicIndicatesMinor(media.demographic ?? {}) ? (
            <Alert variant="destructive">
              <AlertTitle>{t('Minor detected')}</AlertTitle>
              <AlertDescription>
                {t('An age scan found someone under 18. Review this media before others can see it.')}
              </AlertDescription>
            </Alert>
          ) : null}
          <Accordion className="w-full" multiple>
            <AccordionItem value="nsfw-scan">
              <AccordionTrigger>{t('NSFW results')}</AccordionTrigger>
              <AccordionContent>
                <pre className="w-full overflow-x-auto whitespace-pre-wrap rounded-md bg-muted p-2 text-sm">
                  {formatScanPayload(t, media.nsfwScan?.results)}
                </pre>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="ai-scan">
              <AccordionTrigger>{t('AI scan results')}</AccordionTrigger>
              <AccordionContent>
                <pre className="w-full overflow-x-auto whitespace-pre-wrap rounded-md bg-muted p-2 text-sm">
                  {formatScanPayload(t, media.aiDetection?.results)}
                </pre>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="demographic-scan">
              <AccordionTrigger>{t('Demographics results')}</AccordionTrigger>
              <AccordionContent>
                <pre className="w-full overflow-x-auto whitespace-pre-wrap rounded-md bg-muted p-2 text-sm">
                  {formatScanPayload(t, media.demographic?.results)}
                </pre>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <div className="flex w-full items-center justify-between gap-2">
            <NativeSelect
              className="min-w-0 flex-1"
              onChange={(e) => setSelectedRating(e.currentTarget.value as NSFWRatingEnum)}
              value={selectedRating}
            >
              {ratingOptions.map((option) => (
                <NativeSelectOption key={option.value} value={option.value}>
                  {option.label}
                </NativeSelectOption>
              ))}
            </NativeSelect>
            <Button
              className="shrink-0 bg-green-700"
              disabled={selectedRating === 'UNDEFINED'}
              onClick={handleSaveNSFWRating}
              type="button"
              variant="default"
            >
              Approve
            </Button>
          </div>

          <Card className="relative w-full bg-black">
            <CardContent className="flex w-full flex-col overflow-hidden p-0">
              <AspectRatio className="max-h-[80dvh] w-full" ratio={ratio}>
                <button
                  className="relative block h-full w-full cursor-pointer border-0 bg-transparent p-0"
                  onClick={handleImageClick}
                  type="button"
                >
                  <Image
                    alt=""
                    className={cn(
                      'h-full w-full rounded-sm object-contain transition-[filter] duration-300 ease-in-out',
                      completed && 'brightness-[0.4]'
                    )}
                    src={media?.imageUrlMedium ?? undefined}
                  />
                  {media.mediaType === 'VIDEO' ? (
                    <PlayIcon
                      aria-hidden
                      className={cn(
                        'pointer-events-none absolute top-1/2 left-1/2 w-[30%] -translate-x-1/2 -translate-y-1/2 text-white transition-opacity duration-300',
                        completed ? 'opacity-30' : 'opacity-100'
                      )}
                      fill="white"
                    />
                  ) : null}

                  {media.mediaType === 'VIDEO' ? (
                    <Badge
                      className={cn(
                        'absolute right-2 bottom-2 bg-black text-white transition-opacity duration-300',
                        completed ? 'opacity-50' : 'opacity-100'
                      )}
                    >
                      {videoDurationStr(media.videoDuration)}
                    </Badge>
                  ) : null}

                  {!!completed && approvedRating ? (
                    <div
                      className={cn(
                        'pointer-events-none absolute bottom-[1%] left-1/2 z-2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-lg border-2 px-4 py-2 font-bold text-xl shadow-lg',
                        getRatingDisplayInfo(approvedRating).boxClass
                      )}
                    >
                      <span className="text-white" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.5)' } as CSSProperties}>
                        {getRatingDisplayInfo(approvedRating).label}
                      </span>
                    </div>
                  ) : null}
                </button>
              </AspectRatio>
            </CardContent>
          </Card>

          <div className="flex w-full justify-center">
            <Button disabled={completed} onClick={() => setReportModal(true)} type="button">
              Reject
            </Button>
          </div>
        </CardContent>

        <ReportCreate
          onOpenChange={(e) => setReportModal(e.open)}
          onReportCreated={handleRejectMedia}
          open={reportModal}
          targetId={media?.id}
          targetKind={isAttached ? 'ATTACHED_MEDIA' : 'MEDIA'}
        />
      </Card>

      <SimpleMediaImageViewer
        attachedMedia={isAttached}
        imageIndex={0}
        mediaArray={mediaArray}
        onOpenChange={(e) => setMediaViewerOpen(e.open)}
        open={mediaViewerOpen}
      />
    </>
  )
}

export const Route = createFileRoute('/(auth)/(admin-layout)/admin/review/media')({
  component: ReviewMediaPage,
  validateSearch: z.object({
    page: z.number().optional(),
    sortBy: z.string().optional(),
    mediaType: z.string().optional(),
    mediaCategory: z.string().optional(),
  }),
  search: {
    middlewares: [
      // Removes parameters if they match default values
      stripSearchParams({
        page: 1,
        sortBy: 'OLDEST',
        mediaType: 'ALL',
        mediaCategory: 'MEDIA',
      }),
    ],
  },
  head: () =>
    seo({
      title: `Admin - Review Media | ${env.VITE_APP_NAME}`,
      description: 'Admin Review Media Page',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
      meta: [{ name: 'rating', content: 'adult' }],
    }),
})

function ReviewMediaPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const search = useSearch({ strict: false })
  const pageInt = search.page || 1
  const [activeTab, setActiveTab] = useState(search.mediaCategory || 'MEDIA')

  const sortBy = search.sortBy ? search.sortBy : 'OLDEST'
  const mediaTypeFilter = search?.mediaType !== 'ALL' ? search?.mediaType : undefined
  const mediaCategoryFilter = search.mediaCategory || 'MEDIA'

  const variables: AdminReviewMediaQueryVariables = {
    page: pageInt,
    filter: {
      mediaType: mediaTypeFilter as MediaFilter['mediaType'],
    },
    sortBy: sortBy as MediaSortEnum,
    attachedFilter: {
      mediaType: mediaTypeFilter as AttachedMediaFilter['mediaType'],
    },
    attachedSortBy: sortBy as AttachedMediaSortEnum,
  }

  const queryResult = useQuery(ADMIN_REVIEW_MEDIA_QUERY, {
    variables,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  })

  const { data, error, networkStatus } = queryResult

  const formMethods = useForm({
    defaultValues: {
      sortBy: sortBy,
      mediaType: mediaTypeFilter,
      mediaCategory: mediaCategoryFilter,
    },
  })

  useEffect(() => {
    formMethods.reset({
      sortBy: sortBy,
      mediaType: mediaTypeFilter,
      mediaCategory: mediaCategoryFilter,
    })
    setActiveTab(mediaCategoryFilter)
  }, [sortBy, mediaTypeFilter, mediaCategoryFilter, formMethods])

  const handleSortBy = (event: ChangeEvent<HTMLSelectElement>) => {
    navigate({
      search: {
        ...search,
        sortBy: event.target.value,
      },
      replace: true,
    } as never)
  }

  const handleMediaType = (event: ChangeEvent<HTMLSelectElement>) => {
    navigate({
      search: {
        ...search,
        mediaType: event.target.value,
      },
      replace: true,
    } as never)
  }

  const handleTabChange = (value: string) => {
    setActiveTab(value)
    navigate({
      search: {
        ...search,
        mediaCategory: value,
      },
      replace: true,
    } as never)
  }

  const handleLoadMore = () => {
    // Scroll to top of the window
    window.scrollTo({
      top: 0,
      behavior: 'instant',
    })

    // Refetch the data
    queryResult.refetch()
  }

  const isInitialLoading = networkStatus === NetworkStatus.loading
  if (isInitialLoading) return <SFPageLoading />
  if (error) return <ErrorAlert error={error} />

  const mediaList = data?.mediaList ?? null
  const attachedMediaList = data?.attachedMediaList ?? null

  return (
    <div className="mx-auto w-full max-w-screen-2xl px-2 pt-4 md:px-4">
      <Form formMethods={formMethods}>
        <div className="flex w-full flex-col gap-2">
          <Tabs className="w-full" onValueChange={handleTabChange} value={activeTab}>
            <TabsList className="w-full justify-start" variant="tabs">
              <TabsTrigger value="MEDIA">{t('Media')}</TabsTrigger>
              <TabsTrigger value="ATTACHEDMEDIA">{t('Attached Media')}</TabsTrigger>
            </TabsList>

            <div className="mx-auto flex w-full w-full flex-row items-end gap-4 py-4">
              <div className="min-w-0 flex-1">
                <NativeSelectFormField label={t('Sort By')} name="sortBy" onValueChange={handleSortBy}>
                  <option value="LATEST">{t('Latest')}</option>
                  <option value="OLDEST">{t('Oldest')}</option>
                  <option value="VIEWS">{t('Views')}</option>
                </NativeSelectFormField>
              </div>
              <div className="min-w-0 flex-1">
                <NativeSelectFormField label={t('Media Type')} name="mediaType" onValueChange={handleMediaType}>
                  <option value="ALL">{t('All')}</option>
                  <option value="IMAGE">{t('Images')}</option>
                  <option value="VIDEO">{t('Videos')}</option>
                </NativeSelectFormField>
              </div>
            </div>
            <Separator className="w-full" />

            <TabsContent className="mt-2" value="MEDIA">
              <div className="flex flex-wrap justify-around gap-6">
                {mediaList?.medias?.length ? null : <p>{t('No media to review')}</p>}
                {mediaList?.medias?.map((media) => (
                  <ReviewMediaCard isAttached={false} key={media.id} media={media} />
                ))}
                <div className="flex w-full justify-center">
                  <Button onClick={handleLoadMore} type="button" variant="outline">
                    {t('Load More...')}
                  </Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent className="mt-2" value="ATTACHEDMEDIA">
              <div className="flex flex-wrap justify-around gap-6">
                {attachedMediaList?.medias?.length ? null : <p>{t('No attached media to review')}</p>}
                {attachedMediaList?.medias?.map((media) => (
                  <ReviewMediaCard isAttached={true} key={media.id} media={media} />
                ))}
                <div className="flex w-full justify-center">
                  <Button onClick={handleLoadMore} type="button" variant="outline">
                    {t('Load More...')}
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </Form>
    </div>
  )
}
